import { useEffect, useState } from "react";

import { TTourCloseReason } from "@amphore-dev/amphore-lib";

import { useMutation, useQueryClient } from "@tanstack/react-query";

import UserService from "@/services/UserService";

import { useUserSettings } from "@/hooks/useUserSettings";

import { meKeys } from "@/constants/CQueryKeys";

import { TTutorialKey, TTutorialStatus, TUserSettings } from "@/types";

/**
 * One guided tour (the lib's `<Tour>`) and whether the person has seen
 * it (`tutorials` in `GET /me/settings`). It opens by itself while the
 * API says "not seen" (null): right after the onboarding, or after
 * `replay()`. However it ends, the way it ended is recorded so it does
 * not come back - except when a step's target never showed up
 * ("missing"), a bug rather than a choice of the person.
 *
 * The cache is written before the call returns: a tour that just closed
 * must not reopen while the request is in flight. A failed save is not
 * rolled back for the same reason; the API still says "not seen", so it
 * shows again on the next load.
 */
export const useTour = (key: TTutorialKey) => {
	const queryClient = useQueryClient();
	const { settings } = useUserSettings();
	// undefined: settings not loaded yet. null: not seen.
	const state = settings?.tutorials?.[key];
	const [open, setOpen] = useState(false);

	useEffect(() => {
		if (state === null) setOpen(true);
	}, [state]);

	const record = useMutation({
		mutationFn: (status: TTutorialStatus | null) =>
			UserService.updateSettings({ tutorials: { [key]: status } }),
		onMutate: (status) => {
			queryClient.setQueryData<TUserSettings>(meKeys.settings, (old) =>
				old
					? {
							...old,
							tutorials: {
								...old.tutorials,
								[key]: status
									? { status, at: new Date().toISOString() }
									: null,
							},
						}
					: old
			);
		},
		onSuccess: (saved) => {
			queryClient.setQueryData(meKeys.settings, saved);
		},
	});

	const finish = (reason: TTourCloseReason) => {
		setOpen(false);
		if (reason === "missing") return;
		record.mutate(reason === "done" ? "done" : "skipped");
	};

	/** Forgets the tour: it opens again wherever it is mounted. */
	const replay = () => record.mutateAsync(null);

	return { open, finish, replay };
};
